import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { JsonLd } from "@/components/site/JsonLd";
import { CtaBand, FaqSection, LocationCard, TrustBar } from "@/components/site/Sections";
import { SiteLayout } from "@/components/site/SiteLayout";
import { WhatsAppButton } from "@/components/site/WhatsAppChat";
import { getPage, servicePages } from "@/data/pages";
import { business } from "@/lib/business";
import { breadcrumbSchema, canonicalLink, faqSchema, metaFor, serviceSchema } from "@/lib/seo";

export const Route = createFileRoute("/$slug")({
  loader: ({ params }) => {
    const page = getPage(params.slug);
    if (!page) throw notFound();
    return { page };
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Página não encontrada | Chavel Chaveiro Betim" }, { name: "robots", content: "noindex" }] };
    }
    const path = `/${params.slug}`;
    return {
      meta: metaFor({ title: loaderData.page.title, description: loaderData.page.description, path, type: "article" }),
      links: canonicalLink(path),
    };
  },
  component: ServicePageView,
});

function ServicePageView() {
  const { page } = Route.useLoaderData();
  const related = page.related
    .map((slug) => servicePages.find((item) => item.slug === slug))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const url = `/${page.slug}`;

  return (
    <SiteLayout>
      <JsonLd data={serviceSchema(page.keyword, page.description, url)} />
      <JsonLd data={faqSchema(page.faq)} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Início", url: "/" },
          { name: "Serviços", url: "/servicos" },
          { name: page.keyword, url },
        ])}
      />

      <section className="bg-surface text-surface-foreground">
        <div className="container-page py-14">
          <nav aria-label="Trilha de navegação" className="text-xs opacity-75">
            <Link to="/" className="hover:underline">Início</Link>
            {" / "}
            <Link to="/servicos" className="hover:underline">Serviços</Link>
            {" / "}
            <span>{page.keyword}</span>
          </nav>
          <h1 className="mt-4 max-w-4xl font-display text-3xl leading-tight md:text-4xl">{page.h1}</h1>
          <p className="mt-4 max-w-3xl text-base opacity-90">{page.lead}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <WhatsAppButton variant="gold">Falar com a Chavel</WhatsAppButton>
            <a
              href={`tel:${business.phoneRaw}`}
              className="inline-flex items-center rounded-full border border-white/30 px-6 py-3 text-sm font-semibold hover:bg-white/10"
            >
              Ligar {business.phone}
            </a>
          </div>
        </div>
      </section>

      <TrustBar />

      <div className="container-page grid gap-10 py-12 lg:grid-cols-[1.6fr_0.9fr]">
        <article className="prose-article max-w-none">
          <div className="rounded-xl border-l-4 border-gold bg-muted/50 p-5">
            <h2 className="!mt-0 text-lg">Resposta rápida</h2>
            <p className="!mb-0 text-sm">{page.quickAnswer}</p>
          </div>

          {page.sections.map((section) => (
            <section key={section.h2}>
              <h2>{section.h2}</h2>
              {section.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
              {section.bullets ? (
                <ul>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              ) : null}
              {section.h3?.map((item) => (
                <div key={item.title}>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              ))}
            </section>
          ))}

          {related.length ? (
            <section>
              <h2>Serviços relacionados</h2>
              <ul>
                {related.map((item) => (
                  <li key={item.slug}>
                    <Link to="/$slug" params={{ slug: item.slug }}>
                      {item.keyword}
                    </Link>{" "}
                    — {item.description}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </article>

        <aside className="space-y-6">
          <LocationCard />
          <div className="rounded-xl border border-border bg-card p-6">
            <h2 className="text-lg font-semibold">Atendimento imediato</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Descreva o serviço e o bairro no chat: enviamos direto para o WhatsApp da loja com tudo preenchido.
            </p>
            <WhatsAppButton className="mt-4 w-full">Iniciar conversa</WhatsAppButton>
          </div>
        </aside>
      </div>

      <FaqSection items={page.faq} title={`Perguntas frequentes — ${page.keyword}`} />

      <CtaBand
        title="Chavel Chaveiro, tradição em Betim desde 1975"
        text="Orçamento antes do serviço, técnicos próprios e atendimento 24 horas em emergências."
      />
    </SiteLayout>
  );
}
